import { useMemo, useRef } from 'react';
import { Modal, Button, Space, Alert } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import DataMatrixImage from './DataMatrixImage';
import { hasCryptoTail } from '../../utils/dataMatrix';
import type { MarkingCode } from '../../api/marking';
import './MarkingPrintPreview.css';

/**
 * Открывает новое окно с разметкой предпросмотра и вызывает в нём print().
 * Canvas DM-кодов сериализуются в data URL и вставляются как <img>.
 */
const openPrintWindow = (printArea: HTMLElement) => {
  const clone = printArea.cloneNode(true) as HTMLElement;
  // Заменяем canvas → img(data URL), иначе в новом окне они будут пустыми
  const sourceCanvases = printArea.querySelectorAll('canvas');
  const clonedCanvases = clone.querySelectorAll('canvas');
  clonedCanvases.forEach((c, i) => {
    const src = sourceCanvases[i] as HTMLCanvasElement | undefined;
    if (!src) return;
    const img = document.createElement('img');
    img.src = src.toDataURL('image/png');
    img.style.cssText = (c as HTMLCanvasElement).style.cssText;
    img.className = (c as HTMLCanvasElement).className;
    c.replaceWith(img);
  });

  const w = window.open('', '_blank', 'width=900,height=1100');
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Печать</title>
<style>
  @page { size: A4 portrait; margin: 0; }
  html, body { margin: 0; padding: 0; background: #fff; }
  .marking-print-page {
    width: 210mm; height: 297mm; padding: 10mm; box-sizing: border-box;
    page-break-after: always; break-after: page;
  }
  .marking-print-page:last-child { page-break-after: auto; break-after: auto; }
  .marking-print-grid {
    display: grid; grid-template-columns: 1fr 1fr; grid-template-rows: repeat(3, 1fr);
    gap: 4mm; width: 100%; height: 277mm;
  }
  .marking-print-cell {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 3mm; box-sizing: border-box; break-inside: avoid;
  }
  .marking-print-dm { display: flex; align-items: center; justify-content: center; margin-bottom: 2mm; }
  .marking-print-dm img { max-width: 50mm; max-height: 50mm; height: auto; width: auto; }
  .marking-print-label { text-align: center; font-family: sans-serif; line-height: 1.2; word-break: break-word; }
  .marking-print-name { font-size: 11pt; margin-bottom: 1mm; }
  .marking-print-size { font-size: 14pt; font-weight: 700; }
  .marking-print-cell--empty { visibility: hidden; }
</style></head><body>${clone.innerHTML}</body></html>`);
  w.document.close();
  // Ждём отрисовки и вызываем print
  w.onload = () => {
    w.focus();
    w.print();
    // Закрываем после печати (пользователь отменяет → тоже закроется)
    setTimeout(() => w.close(), 300);
  };
};

interface Props {
  open: boolean;
  scans: MarkingCode[];
  onClose: () => void;
}

const PER_PAGE = 6;

const chunk = <T,>(arr: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

/**
 * Модалка с предпросмотром печати Data Matrix кодов.
 * Layout: A4, 6 кодов на страницу (2 столбца × 3 строки).
 * Печать односторонняя — пользователь ставит галочку в диалоге браузера.
 * В диалоге печати браузера доступен выбор страниц.
 */
const MarkingPrintPreview: React.FC<Props> = ({ open, scans, onClose }) => {
  const pages = useMemo(() => chunk(scans, PER_PAGE), [scans]);
  const printAreaRef = useRef<HTMLDivElement>(null);
  const noCrypto = useMemo(() => scans.filter((s) => !hasCryptoTail(s.raw_code)), [scans]);

  const handlePrint = () => {
    if (noCrypto.length > 0) return;
    if (printAreaRef.current) openPrintWindow(printAreaRef.current);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width="90vw"
      style={{ top: 20 }}
      title={`Предпросмотр печати (${scans.length} ${scans.length === 1 ? 'код' : 'кодов'}, ${pages.length} ${pages.length === 1 ? 'страница' : 'стр.'})`}
      className="marking-print-modal"
      footer={
        <Space>
          <Button onClick={onClose}>Отмена</Button>
          <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint} disabled={noCrypto.length > 0}>
            Печать
          </Button>
        </Space>
      }
    >
      {noCrypto.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Марки без криптохвоста напечатать нельзя (${noCrypto.length} шт.)`}
          description="Уберите их из выбора. Такие марки пришли из ШУТ 1С без криптохвоста — Честный Знак их не примет."
        />
      )}
      <div className="marking-print-area" ref={printAreaRef}>
        {pages.map((pageScans, pageIdx) => (
          <div className="marking-print-page" key={pageIdx}>
            <div className="marking-print-grid">
              {pageScans.map((s) => (
                <div className="marking-print-cell" key={s.id}>
                  <div className="marking-print-dm">
                    <DataMatrixImage code={s.raw_code} scale={4} />
                  </div>
                  <div className="marking-print-label">
                    <div className="marking-print-name">
                      {s.brand ? <strong>{s.brand}</strong> : null}
                      {s.brand && s.product_name ? ' ' : ''}
                      {s.product_name || (s.gtin ? `GTIN: ${s.gtin}` : '—')}
                    </div>
                    {s.container_size && (
                      <div className="marking-print-size">{s.container_size}</div>
                    )}
                  </div>
                </div>
              ))}
              {/* Заполнители до 6 клеток для сохранения сетки */}
              {Array.from({ length: PER_PAGE - pageScans.length }).map((_, i) => (
                <div className="marking-print-cell marking-print-cell--empty" key={`empty-${i}`} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
};

export default MarkingPrintPreview;
